import React, { useState, useEffect } from 'react';
import { useEmployee } from '../contexts/EmployeeContext';
import { ClockIcon, PlayIcon, StopIcon, UserIcon } from '@heroicons/react/24/outline';

export const TimeClock: React.FC = () => {
  const { 
    employee, 
    isClockedIn, 
    currentTimeEntry, 
    todayMinutes,
    clockIn, 
    clockOut,
    lock,
    logout 
  } = useEmployee();

  const [loading, setLoading] = useState(false);
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [showMenu, setShowMenu] = useState(false);

  // Calculate elapsed time since clock in
  useEffect(() => {
    if (!isClockedIn || !currentTimeEntry) {
      setElapsedMinutes(0);
      return;
    }

    const updateElapsed = () => {
      const clockInTime = new Date(currentTimeEntry.clock_in).getTime();
      const now = Date.now();
      const minutes = Math.max(0, Math.floor((now - clockInTime) / 60000));
      setElapsedMinutes(minutes);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isClockedIn, currentTimeEntry]);

  const handleClockIn = async () => {
    setLoading(true);
    try {
      await clockIn();
    } catch (error: any) {
      console.error('Clock in failed:', error);
      alert(error.response?.data?.error || 'Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      await clockOut();
    } catch (error: any) {
      console.error('Clock out failed:', error);
      alert(error.response?.data?.error || 'Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const total = Math.max(0, minutes);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (!employee) {
    return null;
  }

  return (
    <div className="relative">
      {/* Main display button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
      >
        <UserIcon className="w-5 h-5 text-slate-600" />
        <div className="text-left">
          <div className="text-sm font-medium text-slate-900">
            {employee.first_name} {employee.last_name}
          </div>
          <div className="flex items-center gap-1 text-xs">
            {isClockedIn ? (
              <>
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-green-700">
                  Working: {formatDuration(elapsedMinutes)}
                </span>
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-slate-400" />
                <span className="text-slate-500">Not clocked in</span>
              </>
            )}
          </div>
        </div>
      </button>

      {/* Dropdown menu */}
      {showMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            {/* Employee info header */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="font-medium text-slate-900">
                {employee.first_name} {employee.last_name}
              </div>
              {employee.employee_number && (
                <div className="text-xs text-slate-500">
                  #{employee.employee_number}
                </div>
              )}
            </div>

            {/* Time status */}
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                <ClockIcon className="w-4 h-4" />
                Today's Hours
              </div>
              <div className="text-2xl font-semibold text-slate-900">
                {formatDuration(todayMinutes + (isClockedIn ? elapsedMinutes : 0))}
              </div>
              {isClockedIn && (
                <div className="text-xs text-green-600 mt-1">
                  Currently working: {formatDuration(elapsedMinutes)}
                </div>
              )}
            </div>

            {/* Clock in/out button */}
            <div className="p-3">
              {isClockedIn ? (
                <button
                  onClick={handleClockOut}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <StopIcon className="w-5 h-5" />
                  {loading ? 'Clocking Out...' : 'Clock Out'}
                </button>
              ) : (
                <button
                  onClick={handleClockIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <PlayIcon className="w-5 h-5" />
                  {loading ? 'Clocking In...' : 'Clock In'}
                </button>
              )}
            </div>

            {/* Additional actions */}
            <div className="border-t border-slate-200 p-2">
              <button
                onClick={() => {
                  // Lock keeps employee in memory - good for quick breaks
                  // Same employee can unlock quickly, or different employee can take over
                  lock();
                  setShowMenu(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Lock Screen
              </button>
              <button
                onClick={async () => {
                  // Logout clears employee completely - prompts about clock out
                  if (isClockedIn) {
                    const clockOutFirst = window.confirm(
                      'You are still clocked in. Would you like to clock out first?\n\n' +
                      'Click OK to clock out, or Cancel to stay clocked in.'
                    );
                    if (clockOutFirst) {
                      await handleClockOut();
                    }
                  }
                  logout();
                  setShowMenu(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Log Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
